import Tippy from '@tippyjs/react/headless';
import clsx from 'clsx';
import { motion, useSpring } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Instance } from 'tippy.js';

import { Icon } from '../Icon';
import { VisuallyHidden } from '../VisuallyHidden';
import styles from './Tooltip.module.scss';

import type { TooltipProps } from './Tooltip.types';

export const defaultTriggerText = 'More information';
export const tooltipTestId = 'tippy-content';
export const triggerTestId = 'tippy-trigger';

export const Tooltip = ({
  content,
  isVisible: controlledIsVisible = false,
  onClickOutside,
  onClose,
  placement = 'auto',
  showOnHover = false,
  tooltipStyle = 'default',
  triggerRef,
  triggerText = defaultTriggerText,
}: TooltipProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(controlledIsVisible);
  const showClose = !showOnHover && tooltipStyle !== 'condensed';
  const opacity = useSpring(0, { damping: 15, stiffness: 300 });

  const show = () => setIsVisible(true);
  const hide = () => setIsVisible(false);

  const onClickClose = () => {
    hide();
    onClose();
  };

  const onMount = () => {
    opacity.set(1);
  };

  const onHide = ({ unmount }: Instance) => {
    const cleanup = opacity.onChange((value) => {
      if (value <= 0) {
        cleanup();
        unmount();
      }
    });

    opacity.set(0);
  };

  useEffect(() => {
    setIsVisible(controlledIsVisible);
  }, [controlledIsVisible]);

  return (
    <Tippy
      render={(attrs) => (
        <motion.div
          className={clsx(styles['tippy-box'], {
            [styles['condensed']]: tooltipStyle === 'condensed',
            [styles['no-exit-icon']]: showOnHover,
          })}
          tabIndex={-1}
          data-theme={clsx(
            tooltipStyle === 'condensed' ? 'hds-condensed' : 'hds-default'
          )}
          style={{ opacity }}
          {...attrs}
        >
          <div
            data-testid={tooltipTestId}
            className={clsx(styles['tippy-content'], {
              [styles['no-close']]: !showClose,
            })}
          >
            <div className={styles['tooltip__tip']}>
              <div className={styles['tooltip__eyebrow']}>
                {showClose && (
                  <button
                    className={clsx('button', styles['tooltip__close-button'])}
                    onClick={onClickClose}
                    aria-label='Close Tooltip'
                    data-testid='tippy-close'
                    type='button'
                  >
                    <Icon
                      className={clsx(
                        styles['tooltip__close-button__icon'],
                        styles['tooltip__close-button__icon']
                      )}
                      color='white'
                      icon='Close16'
                    />
                  </button>
                )}
              </div>
              <div>{content}</div>
            </div>
          </div>
          <div data-popper-arrow='' className={styles['tippy-arrow']} />
        </motion.div>
      )}
      visible={isVisible}
      interactive={true}
      appendTo={'parent'}
      placement={placement}
      animation={true}
      onClickOutside={onClickOutside ?? hide}
      onHide={onHide}
      onMount={onMount}
      onUntrigger={onClose}
      reference={triggerRef}
    >
      {!triggerRef && (
        <div className='form-element__icon'>
          <button
            data-testid={triggerTestId}
            className={clsx('button', 'button_icon', 'icon-button')}
            aria-expanded={isVisible}
            onClick={isVisible ? hide : show}
            onMouseOver={showOnHover ? show : null}
            onFocus={showOnHover ? show : null}
            onMouseOut={showOnHover ? hide : null}
            onBlur={showOnHover ? hide : null}
            type='button'
          >
            <VisuallyHidden>{triggerText}</VisuallyHidden>
            <Icon className='icon-button__icon icon_small' icon='Info12' />
          </button>
        </div>
      )}
    </Tippy>
  );
};
